import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import {
  chatApi,
  useDeleteChatMessageMutation,
  useEditChatMessageMutation,
  useGetChatConversationQuery,
  useLazyGetChatMessagesQuery,
  useMarkChatMessagePlayedMutation,
  useMarkChatReadMutation,
  useReactChatMessageMutation,
  useScheduleChatMessageMutation,
  useSendChatMessageMutation,
  useUnreactChatMessageMutation,
  useSetChatMessagePinnedMutation,
  useReorderChatPinsMutation,
  useUploadChatAttachmentsMutation,
  useVoteChatPollMutation,
} from "@/services/(my-space)/api.chat";
import { CHAT_LOCAL_CONNECTED, chatHub } from "@/services/websocket/chat-hub";
import { SOCKET_CHAT_EVENTS } from "@/services/websocket/socket.enum";
import { getErrorMessage } from "@/utils/error.utils";
import type {
  ChatMessageType,
  IChatConversationRef,
  IChatDeliveryReceipt,
  IChatLocalMessage,
  IChatMention,
  IChatMessage,
  IChatOutgoingMessage,
  IChatPlayReceipt,
  IChatReadReceipt,
  IChatTypingEvent,
  ChatTypingActivity,
  IChatUploadedFile,
} from "@/types/(my-space)/chat.interface";
import {
  compareChatMessages,
  newClientMessageId,
  upsertMessage,
} from "../_lib/chat-utils";
import { personName } from "../_lib/chat-labels";
import { mentionName, mentionsInBody } from "../_lib/mentions";
import { firstDetailResolved } from "../_lib/mention-queue";
import { myReaction, rollbackReaction, toggleReaction } from "../_lib/reactions";
import {
  applyWhileDetached,
  attachWindow,
  mergePage,
  splitLocal,
} from "../_lib/message-window";
import { mergePlayed, withPlayed } from "../_lib/voice-played";
import { withPins } from "../_lib/pinned";

const TYPING_TTL_MS = 6000;
const TYPING_RESEND_MS = 3000;
const TYPING_IDLE_MS = 4000;

const isVisible = () =>
  typeof document === "undefined" || document.visibilityState === "visible";

const sameMessage = (a: IChatMessage, b: IChatMessage) =>
  a.id === b.id || (b.clientMessageId != null && a.clientMessageId === b.clientMessageId);

/**
 * One open conversation: its messages (kept locally — optimistic sends and
 * socket upserts), detail (RTK cache, read pointers patched from the socket),
 * typing, read marking and every message action.
 *
 * The messages are a window of the conversation (see `_lib/message-window`):
 * attached to the live end, or — after `jumpTo` an older message — detached,
 * growing newer with `loadNewer` until it meets the end again.
 */
export const useChatRoom = (conversationId: number) => {
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const myUserId = useAppSelector(selectUser).user?.id;

  const detailQuery = useGetChatConversationQuery(conversationId, {
    refetchOnMountOrArgChange: true,
  });
  const detail = detailQuery.data;

  /**
   * When this room was opened. A detail response fulfilled after it was
   * requested for this visit (refetchOnMountOrArgChange) — a cached copy from
   * an earlier visit fulfilled before it.
   */
  const [opened, setOpened] = useState(() => ({ conversationId, at: Date.now() }));
  useEffect(() => {
    setOpened((o) =>
      o.conversationId === conversationId ? o : { conversationId, at: Date.now() },
    );
  }, [conversationId]);
  const openedAt = opened.conversationId === conversationId ? opened.at : Infinity;
  /**
   * This visit's first detail has resolved. Its `unreadMentionIds` come from my
   * read pointer as the request found it, so the first mark-read is held until
   * then — sent earlier, it could race the request and empty the list. A
   * failed detail releases it too: reading must not wait on it forever.
   */
  const detailReady = firstDetailResolved({
    fulfilledTimeStamp: detailQuery.fulfilledTimeStamp,
    openedAt,
    isError: detailQuery.isError,
    isFetching: detailQuery.isFetching,
  });
  const detailReadyRef = useRef(detailReady);
  detailReadyRef.current = detailReady;

  const [fetchMessages] = useLazyGetChatMessagesQuery();
  const [sendMessage] = useSendChatMessageMutation();
  const [uploadFiles, { isLoading: isUploading }] = useUploadChatAttachmentsMutation();
  const [editMessage] = useEditChatMessageMutation();
  const [deleteMessage] = useDeleteChatMessageMutation();
  const [votePoll] = useVoteChatPollMutation();
  const [reactMessage] = useReactChatMessageMutation();
  const [unreactMessage] = useUnreactChatMessageMutation();
  const [setPinned] = useSetChatMessagePinnedMutation();
  const [reorderPinsRequest] = useReorderChatPinsMutation();
  const [scheduleMessage, { isLoading: isScheduling }] = useScheduleChatMessageMutation();
  const [markRead] = useMarkChatReadMutation();
  const [markPlayedRequest] = useMarkChatMessagePlayedMutation();

  const [messages, setMessages] = useState<IChatLocalMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const nextBeforeRef = useRef<number | null>(null);
  // Detached window: live messages aren't appended; the newest are a reload away.
  const [isDetached, setIsDetachedState] = useState(false);
  const detachedRef = useRef(false);
  /** Our optimistic messages, kept aside while detached. */
  const stashRef = useRef<IChatLocalMessage[]>([]);
  const [hasUnseenNew, setHasUnseenNew] = useState(false);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);
  const loadingNewerRef = useRef(false);
  const [isJumping, setIsJumping] = useState(false);
  /**
   * Bumped by every bulk change to the window that isn't live traffic (a jump,
   * a newer page, re-attaching): those messages must not pop in or be
   * auto-followed like a message that just arrived.
   */
  const [windowVersion, setWindowVersion] = useState(0);
  /**
   * Bumped whenever the window is replaced (load, jump, back to latest): a
   * page requested for the previous window is dropped when it lands, or it
   * would be stitched onto a different slice of the conversation.
   */
  const epochRef = useRef(0);
  /** userId → when they last signalled, and whether it's a voice note. */
  const [typing, setTyping] = useState<Map<number, { at: number; recording: boolean }>>(new Map());

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const myUserIdRef = useRef(myUserId);
  myUserIdRef.current = myUserId;
  /**
   * Voice notes I played this session. Their dot goes at once, and stays gone
   * if a copy of the message from before the server recorded it lands later.
   */
  const playedRef = useRef(new Set<number>());
  const pendingRef = useRef(new Map<string, IChatOutgoingMessage>());
  const localIdRef = useRef(0);

  const setDetached = useCallback((value: boolean) => {
    detachedRef.current = value;
    setIsDetachedState(value);
    setHasUnseenNew(false);
  }, []);

  /**
   * A live insert/update — a socket event or a send's result. Attached it is
   * upserted; detached it only touches what the window (or stash) holds.
   */
  const applyLive = useCallback((incoming: IChatLocalMessage) => {
    const me = myUserIdRef.current;
    const m =
      me != null && playedRef.current.has(incoming.id)
        ? { ...incoming, playedBy: mergePlayed(incoming.playedBy, me) }
        : incoming;
    if (!detachedRef.current) {
      setMessages((list) => upsertMessage(list, m));
      return;
    }
    stashRef.current = applyWhileDetached(
      { list: [], stash: stashRef.current },
      m,
    ).stash;
    setMessages((list) => applyWhileDetached({ list, stash: [] }, m).list);
  }, []);

  /** Our failed/sending bubbles change state wherever they are (window or stash). */
  const mapLocal = useCallback(
    (fn: (list: IChatLocalMessage[]) => IChatLocalMessage[]) => {
      stashRef.current = fn(stashRef.current);
      setMessages(fn);
    },
    [],
  );

  const lastServerId = useCallback(() => {
    const list = messagesRef.current;
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].id > 0) return list[i].id;
    }
    return null;
  }, []);

  // ── Loading ───────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    setMessages([]);
    setTyping(new Map());
    pendingRef.current.clear();
    stashRef.current = [];
    setDetached(false);
    const epoch = ++epochRef.current;
    try {
      const page = await fetchMessages({ conversationId }).unwrap();
      if (epoch !== epochRef.current) return;
      setMessages([...page.data].sort(compareChatMessages));
      setHasMore(page.meta.hasMore);
      nextBeforeRef.current = page.meta.nextBefore;
    } catch (e) {
      setLoadError(getErrorMessage(e, t("page.load_failed")));
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, fetchMessages, setDetached, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadOlder = useCallback(async () => {
    const before = nextBeforeRef.current;
    if (!hasMore || isLoadingOlder || before == null) return;
    setIsLoadingOlder(true);
    const epoch = epochRef.current;
    try {
      const page = await fetchMessages({ conversationId, before }).unwrap();
      if (epoch !== epochRef.current) return;
      setMessages((list) => mergePage(list, page.data));
      setHasMore(page.meta.hasMore);
      nextBeforeRef.current = page.meta.nextBefore;
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.load_messages")));
    } finally {
      setIsLoadingOlder(false);
    }
  }, [conversationId, fetchMessages, hasMore, isLoadingOlder, t]);

  /** After a reconnect: pull what arrived while we weren't listening. */
  const catchUp = useCallback(async () => {
    // Detached, the newest messages arrive by scrolling down (loadNewer).
    if (detachedRef.current) return;
    const after = lastServerId();
    if (after == null) return;
    try {
      const page = await fetchMessages({ conversationId, after }).unwrap();
      if (detachedRef.current) return;
      setMessages((list) => page.data.reduce(upsertMessage, list));
    } catch {
      // The next event or reconnect tries again.
    }
  }, [conversationId, fetchMessages, lastServerId]);

  // ── Jumping (search results, reply quotes) ───────────────────────────────

  /**
   * Shows message `id`: already in the window → nothing to load. Otherwise
   * the window is replaced by the page centred on it, detached unless that
   * page reaches the newest message. Resolves whether `id` is now shown.
   */
  const jumpTo = useCallback(
    async (id: number) => {
      if (messagesRef.current.some((m) => m.id === id)) return true;
      setIsJumping(true);
      const epoch = ++epochRef.current;
      try {
        const page = await fetchMessages({ conversationId, around: id }).unwrap();
        if (epoch !== epochRef.current) return false;
        const stash = detachedRef.current
          ? stashRef.current
          : splitLocal(messagesRef.current).local;
        if (page.meta.hasNewer) {
          stashRef.current = stash;
          setMessages([...page.data].sort(compareChatMessages));
          setDetached(true);
        } else {
          stashRef.current = [];
          setMessages(attachWindow(page.data, stash));
          setDetached(false);
        }
        setHasMore(page.meta.hasMore);
        nextBeforeRef.current = page.meta.nextBefore;
        setWindowVersion((v) => v + 1);
        const shown = page.data.some((m) => m.id === id);
        // Deleted for me ("Delete chat") or otherwise out of reach.
        if (!shown) toast.error(t("errors.open_message"));
        return shown;
      } catch (e) {
        toast.error(getErrorMessage(e, t("errors.open_message")));
        return false;
      } finally {
        if (epoch === epochRef.current) setIsJumping(false);
      }
    },
    [conversationId, fetchMessages, setDetached, t],
  );

  /** Back to the live end: the newest page, re-attached. */
  const jumpToLatest = useCallback(async () => {
    setIsJumping(true);
    const epoch = ++epochRef.current;
    try {
      const page = await fetchMessages({ conversationId }).unwrap();
      if (epoch !== epochRef.current) return false;
      const stash = detachedRef.current
        ? stashRef.current
        : splitLocal(messagesRef.current).local;
      stashRef.current = [];
      setMessages(attachWindow(page.data, stash));
      setHasMore(page.meta.hasMore);
      nextBeforeRef.current = page.meta.nextBefore;
      setDetached(false);
      setWindowVersion((v) => v + 1);
      return true;
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.load_newer")));
      return false;
    } finally {
      if (epoch === epochRef.current) setIsJumping(false);
    }
  }, [conversationId, fetchMessages, setDetached, t]);

  /** Detached: the next newer page; the last one re-attaches the window. */
  const loadNewer = useCallback(async () => {
    const after = lastServerId();
    if (!detachedRef.current || loadingNewerRef.current || after == null) return;
    loadingNewerRef.current = true;
    setIsLoadingNewer(true);
    const epoch = epochRef.current;
    try {
      const page = await fetchMessages({ conversationId, after }).unwrap();
      if (!detachedRef.current || epoch !== epochRef.current) return;
      if (page.meta.hasMore) {
        setMessages((list) => mergePage(list, page.data));
      } else {
        const stash = stashRef.current;
        stashRef.current = [];
        setMessages((list) => attachWindow(mergePage(list, page.data), stash));
        setDetached(false);
      }
      setWindowVersion((v) => v + 1);
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.load_newer")));
    } finally {
      loadingNewerRef.current = false;
      setIsLoadingNewer(false);
    }
  }, [conversationId, fetchMessages, lastServerId, setDetached, t]);

  // Re-attached: anything sent between the last page and the switch was
  // ignored by the socket handler — pull it now.
  const wasDetachedRef = useRef(false);
  useEffect(() => {
    if (wasDetachedRef.current && !isDetached) void catchUp();
    wasDetachedRef.current = isDetached;
  }, [catchUp, isDetached]);

  // ── Read marking ──────────────────────────────────────────────────────────

  const readTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMarkedRef = useRef(0);
  const scheduleRead = useCallback(() => {
    // Detached, the window's last message isn't the newest: nothing to mark.
    // Before the first detail lands, the effect below retries once it has.
    if (!isVisible() || detachedRef.current || !detailReadyRef.current) return;
    if (readTimer.current) clearTimeout(readTimer.current);
    readTimer.current = setTimeout(() => {
      if (detachedRef.current || !detailReadyRef.current) return;
      const id = lastServerId();
      if (id == null || id <= lastMarkedRef.current) return;
      lastMarkedRef.current = id;
      // Impersonated sessions get a 403 here; reading must not fail loudly.
      markRead({ id: conversationId, messageId: id }).catch(() => undefined);
    }, 600);
  }, [conversationId, lastServerId, markRead]);

  useEffect(() => {
    if (!isLoading && !loadError && detailReady) scheduleRead();
  }, [isLoading, loadError, messages.length, isDetached, detailReady, scheduleRead]);

  useEffect(() => {
    lastMarkedRef.current = 0;
    return () => {
      if (readTimer.current) clearTimeout(readTimer.current);
    };
  }, [conversationId]);

  // On screen: its messages don't bump the header badge or pop up — only
  // while the tab is actually visible. A chat window counts too.
  useEffect(() => {
    let release: (() => void) | null = null;
    const sync = () => {
      if (isVisible()) {
        release ??= chatHub.markOpen(conversationId);
        void catchUp();
        scheduleRead();
      } else {
        release?.();
        release = null;
      }
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      release?.();
    };
  }, [conversationId, catchUp, scheduleRead]);

  // ── Realtime ──────────────────────────────────────────────────────────────

  /** A member's read or delivered pointer moved: patch the detail (ticks). */
  const patchPointer = useCallback(
    (
      userId: number,
      key: "lastReadMessageId" | "lastDeliveredMessageId",
      value: number,
    ) => {
      dispatch(
        chatApi.util.updateQueryData("getChatConversation", conversationId, (draft) => {
          const member = draft.members.find((m) => m.id === userId);
          // Pointers only move forward; an older event arriving late is ignored.
          if (member && (member[key] ?? 0) < value) member[key] = value;
        }),
      );
    },
    [conversationId, dispatch],
  );

  useEffect(
    () =>
      chatHub.subscribe((event, payload) => {
        if (event === CHAT_LOCAL_CONNECTED) {
          void catchUp();
          return;
        }
        const ref = payload as { conversationId?: number };
        if (ref?.conversationId !== conversationId) return;
        switch (event) {
          case SOCKET_CHAT_EVENTS.MESSAGE_NEW: {
            const m = payload as IChatMessage;
            if (
              detachedRef.current &&
              !messagesRef.current.some((x) => sameMessage(x, m)) &&
              !stashRef.current.some((x) => sameMessage(x, m))
            ) {
              // Lands past the window's end: "jump to latest" gets a dot instead.
              setHasUnseenNew(true);
            }
            applyLive(m);
            setTyping((map) => {
              if (!m.sender || !map.has(m.sender.id)) return map;
              const next = new Map(map);
              next.delete(m.sender.id);
              return next;
            });
            scheduleRead();
            break;
          }
          case SOCKET_CHAT_EVENTS.MESSAGE_UPDATED:
            applyLive(payload as IChatMessage);
            break;
          case SOCKET_CHAT_EVENTS.READ: {
            const r = payload as IChatReadReceipt;
            patchPointer(r.userId, "lastReadMessageId", r.lastReadMessageId);
            break;
          }
          case SOCKET_CHAT_EVENTS.DELIVERED: {
            const r = payload as IChatDeliveryReceipt;
            patchPointer(r.userId, "lastDeliveredMessageId", r.lastDeliveredMessageId);
            break;
          }
          case SOCKET_CHAT_EVENTS.PLAYED: {
            const r = payload as IChatPlayReceipt;
            mapLocal((list) => withPlayed(list, r.messageId, r.userId));
            break;
          }
          case SOCKET_CHAT_EVENTS.TYPING: {
            const e = payload as IChatTypingEvent;
            if (e.userId === myUserId) return;
            setTyping((map) => {
              const next = new Map(map);
              if (e.isTyping) next.set(e.userId, { at: Date.now(), recording: e.activity === "recording" });
              else next.delete(e.userId);
              return next;
            });
            break;
          }
          case SOCKET_CHAT_EVENTS.CONVERSATION_REMOVED:
            if ((payload as IChatConversationRef).conversationId === conversationId) {
              toast(t("info.removed_from_conversation"));
              navigate("/chat", { replace: true });
            }
            break;
        }
      }),
    [
      applyLive,
      catchUp,
      conversationId,
      mapLocal,
      myUserId,
      navigate,
      patchPointer,
      scheduleRead,
      t,
    ],
  );

  // A typing event that never got its "stop" (closed tab) expires.
  useEffect(() => {
    if (!typing.size) return;
    const timer = setInterval(() => {
      const cutoff = Date.now() - TYPING_TTL_MS;
      setTyping((map) => {
        const next = new Map([...map].filter(([, v]) => v.at >= cutoff));
        return next.size === map.size ? map : next;
      });
    }, 2000);
    return () => clearInterval(timer);
  }, [typing.size]);

  // ── Typing (outgoing) ─────────────────────────────────────────────────────

  const lastTypingSentRef = useRef<number | null>(null);
  const typingStopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `data` can still be the previous room's for a moment: check it's this one.
  const isSelfRef = useRef(false);
  isSelfRef.current = detail?.id === conversationId && !!detail.isSelf;

  const sendTyping = useCallback(
    (isTyping: boolean, activity: ChatTypingActivity = "typing") => {
      if (isTyping) {
        // Nobody else is in my own notes to see it. (A "stop" still goes out
        // when a "start" did — that one belonged to the room it was sent to.)
        if (isSelfRef.current) return;
        lastTypingSentRef.current = Date.now();
      } else {
        if (lastTypingSentRef.current == null) return;
        lastTypingSentRef.current = null;
      }
      chatHub.sendTyping(conversationId, isTyping, activity);
    },
    [conversationId],
  );

  /**
   * Recording a voice note: "recording…" for the others, refreshed while it
   * lasts (their indicator expires without it), cleared when it ends.
   */
  const recordingTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const setRecording = useCallback(
    (on: boolean) => {
      if (recordingTimer.current) clearInterval(recordingTimer.current);
      recordingTimer.current = null;
      if (!on) {
        sendTyping(false);
        return;
      }
      if (typingStopTimer.current) clearTimeout(typingStopTimer.current);
      sendTyping(true, "recording");
      recordingTimer.current = setInterval(() => sendTyping(true, "recording"), TYPING_RESEND_MS);
    },
    [sendTyping],
  );
  useEffect(
    () => () => {
      if (recordingTimer.current) clearInterval(recordingTimer.current);
    },
    [],
  );

  const onInputChanged = useCallback(
    (text: string) => {
      if (typingStopTimer.current) clearTimeout(typingStopTimer.current);
      if (!text) {
        sendTyping(false);
        return;
      }
      const last = lastTypingSentRef.current;
      if (last == null || Date.now() - last > TYPING_RESEND_MS) sendTyping(true);
      typingStopTimer.current = setTimeout(() => sendTyping(false), TYPING_IDLE_MS);
    },
    [sendTyping],
  );

  useEffect(
    () => () => {
      if (typingStopTimer.current) clearTimeout(typingStopTimer.current);
      sendTyping(false);
    },
    [sendTyping],
  );

  // ── Sending ───────────────────────────────────────────────────────────────

  const me = useMemo(
    () => detail?.members.find((m) => m.id === myUserId) ?? null,
    [detail, myUserId],
  );

  /**
   * Optimistic: the bubble appears at once, then is swapped for the server
   * copy. A failure leaves it with a retry; the retry reuses the
   * clientMessageId so the server can't store it twice.
   */
  /**
   * People tagged from outside the chat (a direct chat's "@" searches the
   * company), so a sent message shows their tag before the server's copy.
   */
  const pickedMentionsRef = useRef(new Map<number, string>());
  const rememberMention = useCallback((tag: IChatMention) => {
    pickedMentionsRef.current.set(tag.userId, tag.name);
  }, []);

  const send = useCallback(
    async (
      message: IChatOutgoingMessage,
      opts: {
        clientId?: string;
        replyTo?: IChatMessage | null;
        localAttachments?: IChatMessage["attachments"];
        localMetadata?: IChatMessage["metadata"];
      } = {},
    ) => {
      // Sending from an older part of the chat takes you back to the end first.
      if (detachedRef.current) await jumpToLatest();
      const reply = opts.replyTo ?? null;
      const outgoing: IChatOutgoingMessage = reply
        ? { ...message, replyToId: reply.id }
        : message;
      const cid = opts.clientId ?? newClientMessageId();
      // Tags show on the optimistic bubble at once, as the server will keep them
      // — members, and anyone tagged from the directory in a direct chat.
      const known = new Map<number, string>(pickedMentionsRef.current);
      for (const m of detail?.members ?? []) known.set(m.id, mentionName(m));
      const tagged = outgoing.mentions?.length
        ? mentionsInBody(
            outgoing.body ?? "",
            outgoing.mentions.flatMap((id) => {
              const name = known.get(id);
              return name ? [{ userId: id, name }] : [];
            }),
          )
        : [];
      const localMetadata = tagged.length
        ? { ...(opts.localMetadata ?? {}), mentions: tagged }
        : (opts.localMetadata ?? null);
      const local: IChatLocalMessage = {
        id: -++localIdRef.current,
        conversationId,
        type: outgoing.type,
        body: outgoing.body ?? null,
        metadata: localMetadata,
        sender: me
          ? {
              id: me.id,
              firstName: me.firstName,
              lastName: me.lastName,
              profileImage: me.profileImage,
            }
          : null,
        replyTo: reply
          ? {
              id: reply.id,
              type: reply.type,
              body: reply.body,
              senderId: reply.sender?.id ?? null,
              senderName: reply.sender ? personName(t, reply.sender) : null,
              isDeleted: reply.isDeleted,
            }
          : null,
        attachments: opts.localAttachments ?? [],
        poll: null,
        reactions: [],
        playedBy: [],
        pinnedAt: null,
        pinnedBy: null,
        clientMessageId: cid,
        isDeleted: false,
        editedAt: null,
        createdAt: new Date().toISOString(),
        sendState: "sending",
      };
      pendingRef.current.set(cid, outgoing);
      if (detachedRef.current) {
        // Couldn't re-attach: it shows once the window reaches the end.
        stashRef.current = upsertMessage(stashRef.current, local);
      } else {
        setMessages((list) => upsertMessage(list, local));
      }
      try {
        const sent = await sendMessage({
          conversationId,
          clientMessageId: cid,
          message: outgoing,
        }).unwrap();
        pendingRef.current.delete(cid);
        applyLive(sent);
      } catch (e) {
        mapLocal((list) =>
          list.map((m) =>
            m.clientMessageId === cid && m.id < 0 ? { ...m, sendState: "failed" } : m,
          ),
        );
        toast.error(getErrorMessage(e, t("errors.send")));
      }
    },
    [applyLive, conversationId, detail?.members, jumpToLatest, mapLocal, me, sendMessage, t],
  );

  const retry = useCallback(
    (m: IChatLocalMessage) => {
      const cid = m.clientMessageId;
      const outgoing = cid ? pendingRef.current.get(cid) : undefined;
      if (!cid || !outgoing) return;
      mapLocal((list) => list.filter((x) => x.clientMessageId !== cid));
      void send(outgoing, {
        clientId: cid,
        localAttachments: m.attachments,
        localMetadata: m.metadata,
      });
    },
    [mapLocal, send],
  );

  const discard = useCallback(
    (m: IChatLocalMessage) => {
      if (m.clientMessageId) pendingRef.current.delete(m.clientMessageId);
      mapLocal((list) => list.filter((x) => x.clientMessageId !== m.clientMessageId));
    },
    [mapLocal],
  );

  /**
   * Uploads the files and builds the message that carries them all (+ caption)
   * — shared by sending now and scheduling. Throws the upload error.
   */
  const uploadMessage = useCallback(
    async (
      type: ChatMessageType,
      files: File[],
      opts: {
        caption?: string;
        durationMs?: number;
        waveform?: number[];
        /** Tags in the caption (ids), sent as given. */
        mentions?: number[];
      } = {},
    ): Promise<{ message: IChatOutgoingMessage; uploaded: IChatUploadedFile[] }> => {
      const uploaded = await uploadFiles(files).unwrap();
      return {
        uploaded,
        message: {
          type,
          body: opts.caption || undefined,
          mentions: opts.caption && opts.mentions?.length ? opts.mentions : undefined,
          attachments: uploaded.map((u) => ({
            fileId: u.fileId,
            fileName: u.fileName,
            durationMs: opts.durationMs,
            waveform: opts.waveform?.length ? opts.waveform : undefined,
          })),
        },
      };
    },
    [uploadFiles],
  );

  /** Uploads, then sends one message carrying every file (+ caption). */
  const uploadAndSend = useCallback(
    async (
      type: ChatMessageType,
      files: File[],
      opts: {
        caption?: string;
        durationMs?: number;
        waveform?: number[];
        mentions?: number[];
        replyTo?: IChatMessage | null;
      } = {},
    ) => {
      if (!files.length) return false;
      try {
        const { message, uploaded } = await uploadMessage(type, files, opts);
        await send(
          message,
          {
            replyTo: opts.replyTo,
            localMetadata:
              type === "voice"
                ? { voice: { durationMs: opts.durationMs, waveform: opts.waveform } }
                : null,
            localAttachments: uploaded.map((u) => ({
              ...u,
              durationMs: opts.durationMs ?? null,
            })),
          },
        );
        return true;
      } catch (e) {
        toast.error(getErrorMessage(e, t("errors.upload")));
        return false;
      }
    },
    [send, t, uploadMessage],
  );

  /**
   * Schedules `message` (files, if any, uploaded first exactly as a send does).
   * Toasts the API's reason on failure; the caller keeps the draft then.
   */
  const schedule = useCallback(
    async (
      message: IChatOutgoingMessage,
      opts: { sendAt: Date; replyTo?: IChatMessage | null; files?: File[] },
    ) => {
      let outgoing = message;
      if (opts.files?.length) {
        try {
          outgoing = (
            await uploadMessage(message.type, opts.files, {
              caption: message.body,
              mentions: message.mentions,
            })
          ).message;
        } catch (e) {
          toast.error(getErrorMessage(e, t("errors.upload")));
          return false;
        }
      }
      try {
        await scheduleMessage({
          conversationId,
          message: {
            ...outgoing,
            ...(opts.replyTo ? { replyToId: opts.replyTo.id } : {}),
            sendAt: opts.sendAt.toISOString(),
          },
        }).unwrap();
        return true;
      } catch (e) {
        toast.error(getErrorMessage(e, t("scheduled.errors.schedule")));
        return false;
      }
    },
    [conversationId, scheduleMessage, t, uploadMessage],
  );

  const edit = useCallback(
    async (m: IChatMessage, body: string) => {
      try {
        const updated = await editMessage({ id: m.id, body }).unwrap();
        applyLive(updated);
      } catch (e) {
        toast.error(getErrorMessage(e, t("errors.edit")));
      }
    },
    [applyLive, editMessage, t],
  );

  const remove = useCallback(
    async (m: IChatMessage) => {
      try {
        const updated = await deleteMessage(m.id).unwrap();
        applyLive(updated);
      } catch (e) {
        toast.error(getErrorMessage(e, t("errors.delete")));
      }
    },
    [applyLive, deleteMessage, t],
  );

  /** Toggle one option: single choice replaces, multiple adds/removes. */
  const vote = useCallback(
    async (m: IChatMessage, optionId: number) => {
      const poll = m.poll;
      if (!poll || myUserId == null) return;
      const mine = poll.options
        .filter((o) => o.voterIds.includes(myUserId))
        .map((o) => o.id);
      const optionIds = mine.includes(optionId)
        ? mine.filter((id) => id !== optionId)
        : poll.allowMultiple
          ? [...mine, optionId]
          : [optionId];
      try {
        const updated = await votePoll({ id: m.id, optionIds }).unwrap();
        applyLive(updated);
      } catch (e) {
        toast.error(getErrorMessage(e, t("errors.vote")));
      }
    },
    [applyLive, myUserId, t, votePoll],
  );

  /**
   * Latest reaction request per message: an older response landing after a
   * newer pick must not overwrite it (the newest response, or the socket's
   * `message_updated`, settles it).
   */
  const reactSeqRef = useRef(new Map<number, number>());

  /**
   * Picks `emoji` on `m`: my current one removes it (DELETE), anything else
   * replaces it (PUT). Optimistic; a failure puts my previous reaction back
   * onto whatever the message holds by then, and toasts the API's reason.
   */
  const react = useCallback(
    async (m: IChatMessage, emoji: string) => {
      if (myUserId == null || m.id <= 0) return;
      const current = messagesRef.current.find((x) => x.id === m.id) ?? m;
      const previous = myReaction(current.reactions, myUserId);
      const { action, emoji: mine } = toggleReaction(current.reactions, myUserId, emoji);
      const setReactions = (fn: (r: IChatMessage["reactions"]) => IChatMessage["reactions"]) =>
        setMessages((list) =>
          list.map((x) => (x.id === m.id ? { ...x, reactions: fn(x.reactions) } : x)),
        );
      setReactions((r) => toggleReaction(r, myUserId, emoji).next);
      const seq = (reactSeqRef.current.get(m.id) ?? 0) + 1;
      reactSeqRef.current.set(m.id, seq);
      try {
        const updated =
          action === "delete"
            ? await unreactMessage(m.id).unwrap()
            : await reactMessage({ id: m.id, emoji: mine! }).unwrap();
        if (reactSeqRef.current.get(m.id) === seq) applyLive(updated);
      } catch (e) {
        if (reactSeqRef.current.get(m.id) === seq) {
          setReactions((r) => rollbackReaction(r, myUserId, previous));
        }
        toast.error(getErrorMessage(e, t("errors.react")));
      }
    },
    [applyLive, myUserId, reactMessage, t, unreactMessage],
  );

  /**
   * I started playing a voice note: someone else's, stored, and once per
   * message per session. The dot goes before the request returns; a failed
   * request (it's idempotent) is simply retried on the next play.
   */
  const markPlayed = useCallback(
    (m: IChatMessage) => {
      if (myUserId == null || m.type !== "voice" || m.id <= 0) return;
      if (m.sender?.id === myUserId || playedRef.current.has(m.id)) return;
      // Already recorded (another device, an earlier visit): nothing to send.
      if (m.playedBy?.includes(myUserId)) return;
      playedRef.current.add(m.id);
      mapLocal((list) => withPlayed(list, m.id, myUserId));
      markPlayedRequest(m.id)
        .unwrap()
        .catch(() => playedRef.current.delete(m.id));
    },
    [mapLocal, markPlayedRequest, myUserId],
  );

  /** Pin / unpin for everyone; loaded bubbles follow the server's pinned list. */
  const togglePin = useCallback(
    async (m: IChatMessage) => {
      if (m.id <= 0) return;
      try {
        const pinned = await setPinned({
          id: m.id,
          conversationId: m.conversationId,
          pinned: !m.pinnedAt,
        }).unwrap();
        mapLocal((list) => withPins(list, pinned));
      } catch (e) {
        toast.error(getErrorMessage(e, t("errors.pin")));
      }
    },
    [mapLocal, setPinned, t],
  );

  /** Drag-and-drop in the pinned list: the new order, for everyone. */
  const reorderPins = useCallback(
    async (messageIds: number[]) => {
      try {
        await reorderPinsRequest({ conversationId, messageIds }).unwrap();
      } catch (e) {
        toast.error(getErrorMessage(e, t("errors.pin")));
      }
    },
    [conversationId, reorderPinsRequest, t],
  );

  // ── Derived ───────────────────────────────────────────────────────────────

  const memberNames = useMemo(() => {
    const names = new Map<number, string>();
    detail?.members.forEach((m) => names.set(m.id, personName(t, m)));
    if (detail?.peer) names.set(detail.peer.id, personName(t, detail.peer));
    return names;
  }, [detail, t]);

  const memberImages = useMemo(() => {
    const images = new Map<number, string | null>();
    detail?.members.forEach((m) => images.set(m.id, m.profileImage));
    if (detail?.peer) images.set(detail.peer.id, detail.peer.profileImage);
    return images;
  }, [detail]);

  const typingLabel = useMemo(() => {
    if (!typing.size || detail?.isSelf) return null;
    const [[userId, { recording }]] = typing.entries();
    if (detail?.type === "direct") return recording ? t("status.recording") : t("status.typing");
    if (typing.size > 1) return t("status.several_typing", { count: typing.size });
    const first = memberNames.get(userId)?.split(" ")[0] ?? t("system.someone");
    return recording
      ? t("status.user_recording", { name: first })
      : t("status.user_typing", { name: first });
  }, [detail?.isSelf, detail?.type, memberNames, t, typing]);

  return {
    myUserId,
    detail,
    /** This visit's first detail has resolved (see `unreadMentionIds`). */
    detailReady,
    detailError: detailQuery.error,
    refetchDetail: detailQuery.refetch,
    messages,
    isLoading: isLoading || (detailQuery.isLoading && !detail),
    loadError,
    reload: load,
    hasMore,
    isLoadingOlder,
    loadOlder,
    isDetached,
    hasUnseenNew,
    isLoadingNewer,
    loadNewer,
    isJumping,
    jumpTo,
    jumpToLatest,
    windowVersion,
    isUploading,
    isScheduling,
    typingLabel,
    memberNames,
    memberImages,
    onInputChanged,
    stopTyping: () => sendTyping(false),
    setRecording,
    send,
    rememberMention,
    retry,
    discard,
    uploadAndSend,
    schedule,
    edit,
    remove,
    vote,
    react,
    markPlayed,
    togglePin,
    reorderPins,
  };
};

export type ChatRoomState = ReturnType<typeof useChatRoom>;
